/**
 * shared/actor.ts — who is asking.
 *
 * Three tiers, and only three: an anonymous reader with a server-minted guest identity, an invited
 * user, and god (kartitripathi@gmail.com). There is no fourth, and there is no "tier" string stored
 * anywhere — every permission question is answered by shared/access.ts from this object.
 *
 * Anonymous readers get a REAL identity, not a null. `gst_` + 16 random bytes buys four things an IP
 * address cannot: a stable subject for GPU quota that survives CGNAT (rate-limiting a whole mobile
 * carrier as one person is not a quota), a seat in a co-read room, a durable reading position, and an
 * upgrade path — on signup the guest's positions and room seats are adopted.
 */

export type ActorKind = "guest" | "user";

export interface Actor {
  kind: ActorKind;
  /** "usr_" + 16B base36, or "gst_" + 16B base36. Also the `actorKey` written on every row. */
  id: string;
  /** Users only. NOT NULL UNIQUE and IMMUTABLE in the database; there is no change-email endpoint. */
  email?: string;
  /** Users only. 2-24 chars of [a-z0-9._-]. */
  handle?: string;
  /**
   * Derived LIVE from `users.email ∈ AB_ADMIN_EMAILS`, per request. Never a column, never a second
   * cookie: an `isAdmin` column set at login means removing an address from the env leaves every
   * existing year-long session holding godmode, and a second credential is a second thing to leak and
   * forget to revoke.
   */
  isAdmin: boolean;
}

/** `Actor | null | undefined` — every predicate in shared/access.ts accepts all three. */
export type MaybeActor = Actor | null | undefined;

export const ACTOR_PREFIX = { user: "usr_", guest: "gst_" } as const;

export const isUser = (a: MaybeActor): a is Actor => !!a && a.kind === "user";
export const isGuest = (a: MaybeActor): a is Actor => !!a && a.kind === "guest";

/**
 * The subject written to `positions.actorKey`, `sessions.actorKey`, `room_members.memberKey`,
 * `regen_events.actorKey` and every rate bucket. It is just the id — the prefix already says which
 * table it came from, so there is nothing to concatenate and nothing to parse back out wrongly.
 */
export const actorKey = (a: MaybeActor): string | null => (a ? a.id : null);

/** "usr_9f…" → "user". Returns null for anything that is not one of our two prefixes. */
export function actorKindOfKey(key: string): ActorKind | null {
  if (key.startsWith(ACTOR_PREFIX.user)) return "user";
  if (key.startsWith(ACTOR_PREFIX.guest)) return "guest";
  return null;
}

export const guestActor = (id: string): Actor => ({ kind: "guest", id, isAdmin: false });

export function userActor(u: { id: string; email: string; handle: string; isAdmin: boolean }): Actor {
  return { kind: "user", id: u.id, email: u.email, handle: u.handle, isAdmin: u.isAdmin };
}

/** Trim + lowercase. The single normalisation applied before an address is stored or compared. */
export const normalizeEmail = (email: string): string => email.trim().toLowerCase();

/**
 * Godmode test.
 *
 * `Set.has(normalizeEmail(email))` — deliberately NOT `crypto.timingSafeEqual`. That function throws
 * `RangeError` on unequal buffer lengths, i.e. for every user whose email is a different length from
 * Karti's, which would 500 every request on the site. An email address is not a secret;
 * `timingSafeEqual` is reserved for the invite code, where a length pre-check is specified.
 *
 * `adminEmails` comes from AB_ADMIN_EMAILS via shared/config.ts, already normalised.
 */
export function isAdminEmail(email: string | null | undefined, adminEmails: ReadonlySet<string>): boolean {
  if (!email) return false;
  return adminEmails.has(normalizeEmail(email));
}

/**
 * What to show next to somebody's name. Guests are ALWAYS rendered with a distinct chip and a dotted
 * ring, and a guest name that normalises to a signed-in member's handle is rejected upstream — a
 * guest can never look like an account. (movies' comment is explicit that a member's name used to be
 * whatever the browser claimed, so anyone could sit in a theater as anyone.)
 */
export const displayKindOf = (a: MaybeActor): "guest" | "user" | "god" =>
  !a ? "guest" : a.kind === "guest" ? "guest" : a.isAdmin ? "god" : "user";
